import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { de } from 'date-fns/locale';
import { parseISO } from 'date-fns';

import axios, { fehlerText } from '../api/http';
import { AuthContext } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import BestaetigungsDialog from '../components/common/BestaetigungsDialog';
import ZeiteintragFormular from '../components/timeEntries/ZeiteintragFormular';
import ZeiteintragFilter from '../components/timeEntries/ZeiteintragFilter';
import ZeiteintragTabelle from '../components/timeEntries/ZeiteintragTabelle';
import ZeiteintragDialog from '../components/timeEntries/ZeiteintragDialog';
import {
  BEARBEITUNGSFRIST_STUNDEN,
  FilterZustand,
  LEERE_FILTER,
  Mitarbeiter,
  Projekt,
  SortierFeld,
  SortierZustand,
  Ueberlappung,
  Zeiteintrag,
  ZeiteintragEingabe
} from '../types/zeiterfassung';
import { alsApiDatum, formatDatum, formatStunden } from '../utils/format';

/** Offene Überschneidung, die der Nutzer bestätigen oder verwerfen kann. */
type UeberlappungsFrage = {
  eingabe: ZeiteintragEingabe;
  konflikt: Ueberlappung | null;
  meldung: string;
  /** Bei einer Änderung die ID des betroffenen Eintrags. */
  eintragId?: number;
};

const TimeEntries = () => {
  const { user, istLeitung, istAdmin } = useContext(AuthContext);
  const { zeigeErfolg, zeigeFehler } = useFeedback();

  const [eintraege, setEintraege] = useState<Zeiteintrag[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [laedt, setLaedt] = useState(true);

  const [projekte, setProjekte] = useState<Projekt[]>([]);
  const [projekteLaden, setProjekteLaden] = useState(false);
  const [mitarbeiter, setMitarbeiter] = useState<Mitarbeiter[]>([]);

  const [seite, setSeite] = useState(0);
  const [proSeite, setProSeite] = useState(25);
  const [filter, setFilter] = useState<FilterZustand>(LEERE_FILTER);
  const [sortierung, setSortierung] = useState<SortierZustand>({
    feld: 'date',
    richtung: 'desc'
  });

  const [bearbeiteter, setBearbeiteter] = useState<Zeiteintrag | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<Zeiteintrag | null>(null);
  const [loeschtGerade, setLoeschtGerade] = useState(false);
  const [ueberlappungsFrage, setUeberlappungsFrage] = useState<UeberlappungsFrage | null>(null);
  const [exportLaeuft, setExportLaeuft] = useState(false);

  // Verhindert, dass die Antwort einer älteren Anfrage eine neuere überschreibt.
  const anfrageZaehler = useRef(0);

  /** Query-Parameter aus Filter, Sortierung und Paginierung. */
  const baueParams = useCallback(
    (mitPaginierung: boolean) => {
      const params: Record<string, string | number> = {
        sortField: sortierung.feld,
        sortDirection: sortierung.richtung
      };

      if (mitPaginierung) {
        params.page = seite + 1;
        params.limit = proSeite;
      } else {
        params.limit = -1;
      }

      if (filter.projectId) params.projectId = filter.projectId;
      if (filter.userId) params.userId = filter.userId;
      if (filter.suche) params.search = filter.suche;

      const von = alsApiDatum(filter.dateFrom);
      const bis = alsApiDatum(filter.dateTo);
      if (von) params.dateFrom = von;
      if (bis) params.dateTo = bis;

      return params;
    },
    [filter, seite, proSeite, sortierung]
  );

  const ladeEintraege = useCallback(async () => {
    const laufendeNummer = ++anfrageZaehler.current;
    setLaedt(true);

    try {
      const antwort = await axios.get('/api/time-entries', { params: baueParams(true) });

      // Zwischenzeitlich wurde eine neuere Anfrage gestartet.
      if (laufendeNummer !== anfrageZaehler.current) return;

      setEintraege(antwort.data.timeEntries ?? []);
      setGesamt(antwort.data.pagination?.total ?? antwort.data.timeEntries?.length ?? 0);
    } catch (error) {
      if (laufendeNummer !== anfrageZaehler.current) return;
      zeigeFehler(fehlerText(error, 'Die Zeiteinträge konnten nicht geladen werden'));
    } finally {
      if (laufendeNummer === anfrageZaehler.current) {
        setLaedt(false);
      }
    }
  }, [baueParams, zeigeFehler]);

  useEffect(() => {
    if (user) {
      ladeEintraege();
    }
  }, [user, ladeEintraege]);

  // Projekte einmalig laden
  useEffect(() => {
    const laden = async () => {
      setProjekteLaden(true);
      try {
        const antwort = await axios.get('/api/projects');
        setProjekte(antwort.data.projects ?? []);
      } catch (error) {
        zeigeFehler(fehlerText(error, 'Die Projekte konnten nicht geladen werden'));
      } finally {
        setProjekteLaden(false);
      }
    };

    laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mitarbeiterliste nur für Admin und Projektleiter – und nur einmal.
  //
  // Vorher gab es hier zwei parallele Ladevorgänge (loadUsers und fetchUsers)
  // sowie eine zusätzliche Abfrage der eigenen Rolle über /api/users/me,
  // obwohl diese schon im AuthContext steht.
  useEffect(() => {
    if (!istLeitung) {
      setMitarbeiter([]);
      return;
    }

    const laden = async () => {
      try {
        const antwort = await axios.get('/api/users');
        setMitarbeiter(antwort.data.users ?? []);
      } catch (error) {
        // Kein Fehler-Toast: die Liste ist nur eine Komfortfunktion.
        console.error('Mitarbeiterliste konnte nicht geladen werden', error);
      }
    };

    laden();
  }, [istLeitung]);

  const aendereFilter = useCallback((aenderung: Partial<FilterZustand>) => {
    setSeite(0);
    setFilter(vorher => ({ ...vorher, ...aenderung }));
  }, []);

  const setzeFilterZurueck = useCallback(() => {
    setSeite(0);
    setFilter(LEERE_FILTER);
  }, []);

  const sortiere = (feld: SortierFeld) => {
    setSeite(0);
    setSortierung(vorher => ({
      feld,
      richtung: vorher.feld === feld && vorher.richtung === 'desc' ? 'asc' : 'desc'
    }));
  };

  /**
   * Legt einen Eintrag an. Meldet das Backend eine Überschneidung (409),
   * wird zurückgefragt, statt die Eingabe stillschweigend zu verwerfen.
   */
  const speichereNeu = async (eingabe: ZeiteintragEingabe): Promise<boolean> => {
    try {
      await axios.post('/api/time-entries', eingabe);
      zeigeErfolg('Zeiteintrag gespeichert');
      await ladeEintraege();
      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setUeberlappungsFrage({
          eingabe,
          konflikt: error.response.data?.ueberlappung ?? null,
          meldung: error.response.data?.message ?? 'Es gibt eine Überschneidung.'
        });
        return false;
      }

      zeigeFehler(fehlerText(error, 'Der Zeiteintrag konnte nicht gespeichert werden'));
      return false;
    }
  };

  const speichereAenderung = async (eingabe: ZeiteintragEingabe): Promise<boolean> => {
    if (!bearbeiteter) return false;

    try {
      await axios.put(`/api/time-entries/${bearbeiteter.id}`, eingabe);
      zeigeErfolg('Zeiteintrag aktualisiert');
      await ladeEintraege();
      return true;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setUeberlappungsFrage({
          eingabe,
          konflikt: error.response.data?.ueberlappung ?? null,
          meldung: error.response.data?.message ?? 'Es gibt eine Überschneidung.',
          eintragId: bearbeiteter.id
        });
        return false;
      }

      zeigeFehler(fehlerText(error, 'Der Zeiteintrag konnte nicht aktualisiert werden'));
      return false;
    }
  };

  /** Übernimmt den Eintrag trotz gemeldeter Überschneidung. */
  const ueberlappungAkzeptieren = async () => {
    if (!ueberlappungsFrage) return;

    const { eingabe, eintragId } = ueberlappungsFrage;
    const nutzlast = { ...eingabe, ueberlappungIgnorieren: true };

    try {
      if (eintragId) {
        await axios.put(`/api/time-entries/${eintragId}`, nutzlast);
        zeigeErfolg('Zeiteintrag aktualisiert');
        setBearbeiteter(null);
      } else {
        await axios.post('/api/time-entries', nutzlast);
        zeigeErfolg('Zeiteintrag gespeichert');
      }

      setUeberlappungsFrage(null);
      await ladeEintraege();
    } catch (error) {
      zeigeFehler(fehlerText(error, 'Der Zeiteintrag konnte nicht gespeichert werden'));
    }
  };

  const loescheBestaetigt = async () => {
    if (!zuLoeschen) return;

    setLoeschtGerade(true);
    try {
      await axios.delete(`/api/time-entries/${zuLoeschen.id}`);
      zeigeErfolg('Zeiteintrag gelöscht');
      setZuLoeschen(null);

      // War es der letzte Eintrag der Seite, eine Seite zurückgehen.
      if (eintraege.length === 1 && seite > 0) {
        setSeite(seite - 1);
      } else {
        await ladeEintraege();
      }
    } catch (error) {
      zeigeFehler(fehlerText(error, 'Der Zeiteintrag konnte nicht gelöscht werden'));
    } finally {
      setLoeschtGerade(false);
    }
  };

  /** Lädt die aktuelle Auswahl als CSV herunter. */
  const exportiere = async () => {
    setExportLaeuft(true);
    try {
      const antwort = await axios.get('/api/time-entries/export', {
        params: baueParams(false),
        responseType: 'blob',
        // Der Export umfasst alle passenden Einträge und darf länger dauern
        // als der Standard-Timeout von 10 Sekunden.
        timeout: 120000
      });

      const url = window.URL.createObjectURL(new Blob([antwort.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `zeiteintraege_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      zeigeErfolg('CSV-Datei wurde erstellt');
    } catch (error) {
      zeigeFehler(fehlerText(error, 'Der Export ist fehlgeschlagen'));
    } finally {
      setExportLaeuft(false);
    }
  };

  /**
   * Mitarbeiter dürfen eigene Einträge nur innerhalb der Frist ändern,
   * Admin und Projektleiter jederzeit.
   */
  const darfBearbeiten = (eintrag: Zeiteintrag): boolean => {
    if (istLeitung) return true;
    if (!eintrag.created_at) return false;

    try {
      const verstrichen = (Date.now() - parseISO(eintrag.created_at).getTime()) / (1000 * 60 * 60);
      return verstrichen <= BEARBEITUNGSFRIST_STUNDEN;
    } catch {
      return false;
    }
  };

  const hatFilter =
    Boolean(filter.projectId) ||
    Boolean(filter.userId) ||
    Boolean(filter.dateFrom) ||
    Boolean(filter.dateTo) ||
    Boolean(filter.suche);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      {/* Der Außenabstand kommt aus dem Layout, hier bewusst kein zusätzliches Padding. */}
      <Box>
        <Typography variant="h4" gutterBottom>
          Zeiterfassung
        </Typography>

        <ZeiteintragFormular
          projekte={projekte}
          projekteLaden={projekteLaden}
          mitarbeiter={mitarbeiter}
          darfFuerAndereErfassen={istLeitung}
          onSpeichern={speichereNeu}
        />

        <ZeiteintragFilter
          filter={filter}
          onFilterAendern={aendereFilter}
          onZuruecksetzen={setzeFilterZurueck}
          projekte={projekte}
          mitarbeiter={mitarbeiter}
          darfMitarbeiterFiltern={istLeitung}
          anzahl={gesamt}
          onExport={exportiere}
          exportLaeuft={exportLaeuft}
        />

        <ZeiteintragTabelle
          eintraege={eintraege}
          laedt={laedt}
          gesamt={gesamt}
          seite={seite}
          proSeite={proSeite}
          sortierung={sortierung}
          zeigeMitarbeiter={istLeitung}
          zeigeErstelltVon={istAdmin}
          hatFilter={hatFilter}
          darfBearbeiten={darfBearbeiten}
          unbegrenztBearbeitbar={istLeitung}
          onSeiteAendern={setSeite}
          onProSeiteAendern={neu => {
            setProSeite(neu);
            setSeite(0);
          }}
          onSortieren={sortiere}
          onBearbeiten={setBearbeiteter}
          onLoeschen={setZuLoeschen}
        />

        <ZeiteintragDialog
          offen={Boolean(bearbeiteter)}
          eintrag={bearbeiteter}
          projekte={projekte}
          projekteLaden={projekteLaden}
          mitarbeiter={mitarbeiter}
          darfMitarbeiterAendern={istLeitung}
          onAbbrechen={() => setBearbeiteter(null)}
          onSpeichern={speichereAenderung}
        />

        <BestaetigungsDialog
          offen={Boolean(zuLoeschen)}
          titel="Zeiteintrag löschen?"
          text={
            zuLoeschen
              ? `Der Eintrag vom ${formatDatum(zuLoeschen.date)} für "${zuLoeschen.projectName}" (${formatStunden(zuLoeschen.hours)}) wird endgültig gelöscht. Das kann nicht rückgängig gemacht werden.`
              : ''
          }
          bestaetigenText="Löschen"
          gefaehrlich
          laedt={loeschtGerade}
          onBestaetigen={loescheBestaetigt}
          onAbbrechen={() => setZuLoeschen(null)}
        />

        <BestaetigungsDialog
          offen={Boolean(ueberlappungsFrage)}
          titel="Überschneidung erkannt"
          text={`${ueberlappungsFrage?.meldung ?? ''} Möchten Sie den Eintrag trotzdem speichern?`}
          bestaetigenText="Trotzdem speichern"
          abbrechenText="Zurück zur Eingabe"
          onBestaetigen={ueberlappungAkzeptieren}
          onAbbrechen={() => setUeberlappungsFrage(null)}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default TimeEntries;
